import L, { divIcon } from 'leaflet';
import React from 'react';
import { renderToString } from 'react-dom/server';
import { IListenerRef, GeomanEditOptions, GeomanDrawOptions } from '../types/geoman';
import { ICON_ANCHOR } from './mapEditControl.utils';

// tslint:disable:no-console

/**
 * Edit mode options for Geoman layers
 * Prevents self-intersecting polygons and disables snapping for consistent behavior
 */
export const EDIT_OPTIONS: GeomanEditOptions = {
  snappable: false,
  allowSelfIntersection: false // Prevent self-intersecting polygons
};

/**
 * Timing constants for async operations
 */
export const TIMING = {
  EDIT_RETRY_DELAY: 100 // Delay for retrying layer.pm.enable() when it fails (handles React-Leaflet timing issues)
} as const;

/**
 * Check if a listener already exists for a layer and event
 * Prevents duplicate event listeners
 *
 * @param listenerRefs - Array of listener references
 * @param layer - The layer to check
 * @param event - The event name to check
 * @returns true if listener exists, false otherwise
 */
export const hasListener = (
  listenerRefs: IListenerRef[],
  layer: L.Layer,
  event: string
): boolean => {
  return listenerRefs.some(ref => ref.layer === layer && ref.event === event);
};

/**
 * Safely get the active draw shape from Geoman Draw API
 * Returns null if Draw API is not available
 *
 * @param map - The Leaflet map instance
 * @returns The active draw shape name or null if not available
 */
export const getActiveDrawShape = (map: L.Map): string | null => {
  try {
    const draw = map.pm?.Draw;
    if (draw && typeof draw.getActiveShape === 'function') {
      return draw.getActiveShape();
    }
  } catch (e) {
    console.warn('Failed to get active draw shape:', e);
  }
  return null;
};

/**
 * Create a marker icon for Geoman draw mode
 * Creates a divIcon with the provided MarkerIcon component and CSS classes
 *
 * @param MarkerIcon - React component for the marker icon
 * @param iconClassName - CSS class name for the icon element
 * @param containerClassName - CSS class name for the container element
 * @returns Leaflet DivIcon instance
 */
export const createMarkerIcon = (
  MarkerIcon: React.ComponentType<{ className?: string }>,
  iconClassName: string,
  containerClassName: string
): L.DivIcon => {
  const renderIcon = React.createElement(MarkerIcon, { className: iconClassName });
  return divIcon({
    className: containerClassName,
    iconAnchor: ICON_ANCHOR,
    html: renderToString(renderIcon)
  });
};

/**
 * Create a marker icon for Geoman draw mode (with iconSize option)
 * Creates a divIcon with the provided MarkerIcon component and CSS classes
 * Used when iconSize needs to be explicitly set to undefined
 *
 * @param MarkerIcon - React component for the marker icon
 * @param iconClassName - CSS class name for the icon element
 * @param containerClassName - CSS class name for the container element
 * @returns Leaflet DivIcon instance
 */
export const createMarkerIconForDraw = (
  MarkerIcon: React.ComponentType<{ className?: string }>,
  iconClassName: string,
  containerClassName: string
): L.DivIcon => {
  const renderIcon = React.createElement(MarkerIcon, { className: iconClassName });
  return divIcon({
    className: containerClassName,
    iconSize: undefined,
    iconAnchor: ICON_ANCHOR,
    html: renderToString(renderIcon)
  });
};

/**
 * Setup marker dragging with cleanup management
 * Handles the common pattern of setting up marker dragging and storing cleanup
 *
 * @param marker - The marker to setup dragging for
 * @param markerCleanupRefs - Map to store cleanup functions
 * @param setupMarkerDragging - Function to setup marker dragging
 * @returns true if setup was successful, false otherwise
 */
export const setupMarkerDraggingWithCleanup = (
  marker: L.Marker,
  markerCleanupRefs: Map<L.Marker, () => void>,
  setupMarkerDragging: (marker: L.Marker) => (() => void) | undefined
): boolean => {
  try {
    const cleanup = setupMarkerDragging(marker);
    if (cleanup) {
      markerCleanupRefs.set(marker, cleanup);
      return true;
    }
  } catch (e) {
    console.warn('Failed to setup marker dragging:', e);
  }
  return false;
};

/**
 * Map AreaTypes to Geoman shape names
 *
 * @param areaType - The area type to convert
 * @returns The corresponding Geoman shape name or null if not found
 */
export const getGeomanShapeName = (areaType: string): string | null => {
  switch (areaType) {
    case 'Box':
      return 'Rectangle';
    case 'Circle':
      return 'Circle';
    case 'Polygon':
      return 'Polygon';
    case 'Marker':
      return 'Marker';
    default:
      return null;
  }
};

/**
 * Get common draw options for Geoman shapes
 * Provides consistent options across different shape types
 *
 * @param additionalOptions - Additional options to merge with defaults
 * @returns GeomanDrawOptions object
 */
export const getCommonDrawOptions = (
  additionalOptions: Partial<GeomanDrawOptions> = {}
): GeomanDrawOptions => ({
  snappable: false,
  cursorMarker: true,
  ...additionalOptions
});
